import { useRoute } from '#imports'
import { computed, ref, shallowReactive } from 'vue'

import type { SearchConfiguration } from '@backmarket/http-api/src/api-specs-search-reco/search/searchConfiguration'
import { getInitialFiltersDict } from '@backmarket/nuxt-layer-recommendation/filtering/getInitialFiltersDict'

function parseQueryValues(
  raw: string | null | Array<string | null> | undefined,
): string[] {
  if (!raw) return []

  return (Array.isArray(raw) ? raw : [raw]).filter(
    (v): v is string => typeof v === 'string' && v.length > 0,
  )
}

export function useSearchInputs(
  searchConfig: SearchConfiguration,
  openFacets: string[] = [],
) {
  const route = useRoute()
  const query = computed(
    () =>
      (Array.isArray(route.query.q) ? route.query.q[0] : route.query.q) ?? '',
  )

  const openFilterKey = openFacets.find((key) => route.query[key] != null)
  const openFilterRaw = openFilterKey ? route.query[openFilterKey] : undefined
  const openFilterValues = parseQueryValues(openFilterRaw)

  const filtersInput = shallowReactive<Record<string, string[]>>({
    ...getInitialFiltersDict(searchConfig.facets),
    ...(openFilterKey && openFilterValues.length > 0
      ? { [openFilterKey]: openFilterValues }
      : {}),
  })
  const sortInput = ref(searchConfig.indexes.active.name)
  const pageInput = ref(
    Number.parseInt(route.query.p?.toString() || '1', 10) || 1,
  )
  const priceInput = ref<[number, number]>()
  const isResetting = ref(false)

  function reset() {
    isResetting.value = true
    Object.assign(filtersInput, getInitialFiltersDict(searchConfig.facets))
    sortInput.value = searchConfig.indexes.active.name
    pageInput.value = 1
    priceInput.value = undefined
    isResetting.value = false
  }

  return {
    query,
    filtersInput,
    sortInput,
    pageInput,
    priceInput,
    reset,
    isResetting,
  }
}
